
import { Button, Col, Form, getValueFromEvent, Icons, Input, InputNumber, Row, Select, Switch, Upload } from "@pankod/refine-antd";
import { useApiUrl, useGetIdentity, useSelect } from "@pankod/refine-core";
import { message, Typography } from "antd";
import { ShowUserCard } from "components/profile/showUserCard";
import { ICountry, ILocations, IUser } from "interfaces";
import { MediaType } from "interfaces/enum";
import React, { useCallback, useEffect, useRef } from "react";
import { DATAPROVIDER_LOOKUP, DATAPROVIDER_UPLOAD, fileUploadValidatorPdfWord, RESOURCE_PATH } from "scripts/site";

// Layout configurations
export const formLayouts = {
  twoColumnLayout: {
    xs: { span: 24 },
    sm: { span: 24 },
    md: { span: 16 },
    lg: { span: 10 },
    xl: { span: 10 },
    xxl: { span: 16 },
  },
  rowGutter: { xs: 8, sm: 16, md: 24, lg: 32, xl: 40, xxl: 48 },
};

// Custom hook for country and location data
export const useCountryLocationData = () => {
  // Select hook for country lookup
  const countrySelectResult = useSelect<ICountry>({
    dataProviderName: DATAPROVIDER_LOOKUP,
    resource: RESOURCE_PATH.COUNTRY,
    optionLabel: "name",
    optionValue: "id",
    sort: [
      {
        field: "name",
        order: "asc",
      },
    ],
    fetchSize: 100,
    filters: [
      {
        field: "backgroundScreening.enabled",
        operator: "eq",
        value: true,
      },
    ],
  });

  // Select hook for location lookup
  const { queryResult: locationProps } = useSelect<ILocations>({
    dataProviderName: DATAPROVIDER_LOOKUP,
    resource: RESOURCE_PATH.LOCATION,
    optionLabel: "name",
    optionValue: "id",
    sort: [
      {
        field: "name",
        order: "asc",
      },
    ],
    fetchSize: 100,
    filters: [
      {
        field: "backgroundScreening.enabled",
        operator: "eq",
        value: true,
      },
    ],
  });

  const locations = locationProps.data?.data;

  return { countryProps: countrySelectResult, locations };
};

const NAME_PATTERN = /^[A-Za-z\s.'-]+$/;

// Validation rules
export const validationRules = {
  firstName: [
    {
      required: true,
      whitespace: true,
      message: "Please enter First Name",
    },
    {
      min: 2,
      max: 50,
      message: "First Name must be between 2 and 50 characters",
    },
    {
      pattern: NAME_PATTERN,
      message: "First Name can only contain letters, spaces, dots, hyphens and apostrophes",
    },
  ],

  lastName: [
    {
      required: true,
      whitespace: true,
      message: "Please enter Last Name",
    },
    {
      min: 2,
      max: 50,
      message: "Last Name must be between 2 and 50 characters",
    },
    {
      pattern: NAME_PATTERN,
      message: "Last Name can only contain letters, spaces, dots, hyphens and apostrophes",
    },
  ],

  associateName: [
    {
      required: true,
      whitespace: true,
      message: "Please enter Associate Name",
    },
    {
      min: 2,
      max: 100,
      message: "Associate Name must be between 2 and 100 characters",
    },
    {
      pattern: NAME_PATTERN,
      message: "Associate Name can only contain letters, spaces, dots, hyphens and apostrophes",
    },
  ],

  tcsEmployeeId: [
    {
      required: true,
      whitespace: true,
      type: "number" as const,
      message: "Please enter TCS Employee Id",
    },
  ],

  personalEmailId: [
    {
      required: true,
      whitespace: true,
      type: "email" as const,
      message: "Please enter a valid Personal Email Id",
    },
  ],

  tcsEmailId: [
    {
      required: true,
      whitespace: true,
      type: "email" as const,
      message: "Please enter a valid TCS Email Id",
    },
    {
      validator: (_: any, value: any) => {
        if (!value) return Promise.resolve();
        const tcsDomains = ["@tcs.com"];
        const isValidDomain = tcsDomains.some(domain => value.endsWith(domain));
        if (!isValidDomain) {
          return Promise.reject("Email must be from TCS domain (@tcs.com)");
        }
        // Check if it's not in employeeid@tcs.com format
        const localPart = value.split("@")[0];
        if (!isNaN(+localPart)) {
          return Promise.reject("Email should not be in employeeid@tcs.com format. Use proper name format");
        }
        return Promise.resolve();
      },
    },
  ],

  countryId: [
    {
      required: true,
      whitespace: true,
      message: "Please select Country",
    },
  ],

  locationId: [
    {
      required: true,
      whitespace: true,
      message: "Please select Location",
    },
  ],

  won: [
    {
      required: true,
      whitespace: true,
      message: "Project WON should be 7 or 8 digits long numeric value starting with 1 or 2",
      validator: async (_: any, value: any) => {
        let strValue = value?.toString();
        if (
          strValue &&
          (strValue.length === 7 || strValue.length === 8) &&
          (strValue.startsWith("1") || strValue.startsWith("2")) &&
          /^\d+$/.test(strValue)
        ) {
          return Promise.resolve();
        } else {
          return Promise.reject("WON does not match the criteria.");
        }
      }
    }
  ],

  msPersonnelNumber: [
    {
      type: "number" as const,
      message: "MS Personnel Number must be numeric",
    },
  ],

  microsoftAlias: [
    {
      required: false,
      whitespace: true,
      message: "Please enter Microsoft Alias",
    },
    {
      pattern: /^v-[a-zA-Z0-9._-]+$/,
      message: "Microsoft Alias must start with 'v-' followed by alphanumeric characters, dots, hyphens, or underscores",
    },
  ],

  remarks: [
    {
      required: false,
      max: 1000,
      message: "Remarks must be less than 1000 characters",
    },
  ],

  consentDocument: (required: boolean = true) => [
    ...(required ? [{
      required: true,
      message: "Please upload Consent Document!",
    }] : []),
  ],

  completedTrainingDocument: (required: boolean = true) => [
    ...(required ? [{
      required: true,
      message: "Please upload Completed Training Document!",
    }] : []),
  ],
};

// Reusable form field components
interface FormFieldsProps {
  form: any;
  countryProps: any;
  locations: any[];
  onUploadChange: any;
}

export const AssociateNameField: React.FC = () => (
  <Row gutter={[16, 0]}>
    <Col xs={24} sm={24} md={12}>
      <Form.Item
        label="First Name"
        name="firstName"
        rules={validationRules.firstName}
      >
        <Input placeholder="Enter First Name" />
      </Form.Item>
    </Col>
    <Col xs={24} sm={24} md={12}>
      <Form.Item
        label="Last Name"
        name="lastName"
        rules={validationRules.lastName}
      >
        <Input placeholder="Enter Last Name" />
      </Form.Item>
    </Col>
  </Row>
);

export const TcsEmployeeIdField: React.FC<{ required?: boolean; disabled?: boolean; showToggle?: boolean; disableToggle?: boolean }> = ({ required = true, disabled = false, showToggle = true, disableToggle = false }) => {
  if (!showToggle) {
    return (
      <>
        <Form.Item name="hasTcsEmployeeId" hidden initialValue={true}>
          <Input />
        </Form.Item>
        <Form.Item
          label="TCS Employee Id"
          name="tcsEmployeeId"
          rules={validationRules.tcsEmployeeId}
          required
          colon={true}
        >
          <InputNumber
            placeholder="Enter TCS Employee Id"
            maxLength={7}
            controls={false}
            style={{ width: "100%" }}
            precision={0}
            disabled={disabled}
          />
        </Form.Item>
      </>
    );
  }

  return (
    <Form.Item label="Has TCS Employee Id?" required={required} colon={true}>
      <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
        <Form.Item
          name="hasTcsEmployeeId"
          valuePropName="checked"
          initialValue={true}
          noStyle
        >
          <Switch checkedChildren="Yes" unCheckedChildren="No" disabled={disableToggle} />
        </Form.Item>
        <div style={{ flex: 1 }}>
          <Form.Item
            name="tcsEmployeeId"
            rules={required ? validationRules.tcsEmployeeId : []}
            noStyle
          >
            <InputNumber
              placeholder="Enter TCS Employee Id"
              maxLength={7}
              controls={false}
              style={{ width: "100%" }}
              precision={0}
              disabled={disabled}
            />
          </Form.Item>
        </div>
      </div>
    </Form.Item>
  );
};

export const TcsEmailIdField: React.FC<{ required?: boolean }> = ({ required = true }) => (

  <Form.Item
    label="TCS Email"
    name="tcsEmailId"
    rules={required ? validationRules.tcsEmailId : []}
    tooltip="Provide TCS Email Id in sample.user@tcs.com format. Please note email Id in format employeeid@tcs.com does not work from external systems."
  >
    <Input placeholder="Enter TCS Email Id" />
  </Form.Item>
);

export const PersonalEmailField: React.FC = () => (
  <Form.Item
    label="Personal Email of Associate"
    name="personalEmailId"
    rules={validationRules.personalEmailId}
    tooltip="Provide a personal email address of associate to receive screening updates."
  >
    <Input placeholder="Enter Personal Email Id of Associate" />
  </Form.Item>
);

export const CountryField: React.FC<{ form: any; countryProps: any }> = ({ form, countryProps }) => (
  <Form.Item
    label="Country"
    name="countryId"
    rules={validationRules.countryId}
  >
    <Select
      allowClear
      placeholder="Select Country"
      showSearch
      {...(countryProps.selectProps || countryProps)}
      onChange={(_value) => {
        form.setFieldsValue({ 'locationId': null });
      }}
    />
  </Form.Item>
);

export const LocationField: React.FC<{ form: any; locations: any[] }> = ({ form, locations }) => (
  <Form.Item
    noStyle
    shouldUpdate={(prevValues, currentValues) => prevValues.countryId !== currentValues.countryId}
  >
    {({ getFieldValue }) =>
      <Form.Item
        label="Location"
        name="locationId"
        rules={validationRules.locationId}
      >
        <Select
          allowClear
          placeholder={form?.getFieldValue('countryId') ? "Select Location" : "Select a Country first"}
          showSearch
        >
          {locations?.filter(loc => loc.countryId === getFieldValue("countryId")).map((m, i) => (
            <Select.Option value={m.id} key={`lc-${i}`}>{m.name}</Select.Option>
          ))}
        </Select>
      </Form.Item>
    }
  </Form.Item>
);

export const WonField: React.FC = () => (

  <Form.Item
    label="WON"
    name="won"
    tooltip="WON should be 7 or 8 digits long numeric value starting with 1 or 2"
    rules={validationRules.won}
  >
    <InputNumber min={7} precision={0} maxLength={8} placeholder="Enter WON" style={{ width: "100%" }} controls={false} />
  </Form.Item>
);

export const MsPersonnelNumberField: React.FC<{ required?: boolean }> = ({ required = false }) => {
  const rules = required
    ? [
        {
          required: true,
          type: "number" as const,
          message: "MS Personnel Number is required when Microsoft Alias is provided",
        },
      ]
    : validationRules.msPersonnelNumber;

  return (
    <Form.Item
      label="MS Personnel Number"
      name="msPersonnelNumber"
      rules={rules}
    >
      <InputNumber
        placeholder="Enter MS Personnel Number"
        controls={false}
        style={{ width: "100%" }}
        precision={0}
      />
    </Form.Item>
  );
};

export const MicrosoftAliasField: React.FC = () => (
  <Form.Item
    label="Microsoft Alias (v-)"
    name="microsoftAlias"
    rules={validationRules.microsoftAlias}
  >
    <Input placeholder="e.g., v-username" />
  </Form.Item>
);

export const RemarksField: React.FC = () => (
  <Form.Item
    label="Remarks"
    name="remarks"
    rules={validationRules.remarks}
  >
    <Input.TextArea
      placeholder="Enter remarks here..."
      rows={3}
      maxLength={1000}
      showCount
    />
  </Form.Item>
);

export const DocumentUploadField: React.FC<{
  name: string;
  label: string;
  buttonText: string;
  onUploadChange: any;
  required?: boolean;
  id?: string;
}> = ({ name, label, buttonText, onUploadChange, required = true, id }) => {
  const apiUrl = useApiUrl(DATAPROVIDER_UPLOAD);
  const formInstance = (Form as any).useFormInstance?.() as any;

  const getExistingFiles = useCallback(() => {
    try {
      const currentValue = formInstance?.getFieldValue(name);
      return Array.isArray(currentValue) ? currentValue : [];
    } catch (error) {
      return [];
    }
  }, [formInstance, name]);

  const lastSuccessfulFilesRef = useRef<any[]>([]);

  useEffect(() => {
    const existingFiles = getExistingFiles();
    if (existingFiles.length) {
      lastSuccessfulFilesRef.current = existingFiles;
    }
  }, [getExistingFiles]);

  const filterErroredFiles = useCallback((fileList: any[] = []) => fileList.filter((file: any) => file?.status !== "error"), []);

  const deriveFileList = useCallback(
    (info: any) => {
      const status = info?.file?.status;
      const fileList = filterErroredFiles(info?.fileList ?? []);

      if (status === "done" || status === "removed") {
        lastSuccessfulFilesRef.current = fileList;
        return fileList;
      }

      if (status === "error") {
        return lastSuccessfulFilesRef.current;
      }

      // For uploading or undefined statuses, keep showing the last successful file until upload succeeds
      if (!fileList.length && lastSuccessfulFilesRef.current.length) {
        return lastSuccessfulFilesRef.current;
      }

      return fileList;
    },
    [filterErroredFiles]
  );

  const normalizeUploadEvent = useCallback(
    (event: any) => {
      const normalizedEvent = {
        ...event,
        fileList: getValueFromEvent(event) ?? [],
      };

      return deriveFileList(normalizedEvent);
    },
    [deriveFileList]
  );

  const handleUploadChange = useCallback(
    (info: any) => {
      const { file } = info || {};

      if (file?.status === "error") {
        message.error("File upload failed. Please try again.");
      }

      const sanitizedList = deriveFileList(info);
      onUploadChange?.({ ...info, fileList: sanitizedList });
    },
    [deriveFileList, onUploadChange]
  );

  // Use id in upload action if provided
  const uploadAction = id
    ? `${apiUrl}/${RESOURCE_PATH.BACKGROUND_SCREENING}/${id}`
    : `${apiUrl}/${RESOURCE_PATH.BACKGROUND_SCREENING}/${MediaType.Document}`;

  return (
    <Form.Item
      label={label}
      name={name}
      valuePropName="fileList"
      getValueFromEvent={normalizeUploadEvent}
      tooltip="Accepted types: PDF, DOC, DOCX, EML, MSG (Max 5MB)"
      rules={name === "consentDocument"
        ? validationRules.consentDocument(required)
        : validationRules.completedTrainingDocument(required)
      }
    >
      <Upload
        name="file"
        accept="application/pdf,.doc,.docx,.eml,.msg"
        action={uploadAction}
        listType="text"
        // Intercept change events so that failed uploads are removed from the fileList
        onChange={handleUploadChange}
        maxCount={1}
        beforeUpload={fileUploadValidatorPdfWord(required)}
      >
        <Button icon={<Icons.UploadOutlined />}>{buttonText}</Button>
      </Upload>
    </Form.Item>
  );
};

// Main form fields section component
export const BackgroundScreeningFormFields: React.FC<FormFieldsProps> = ({
  form,
  countryProps,
  locations,
  onUploadChange
}) => {
  const { twoColumnLayout, rowGutter } = formLayouts;
  const { data: userData } = useGetIdentity();
  const userObj = userData as IUser;
  const firstName = Form.useWatch("firstName", form) as string | undefined;
  const lastName = Form.useWatch("lastName", form) as string | undefined;
  const selectedCountryId = Form.useWatch("countryId", form) as string | undefined;
  const hasTcsEmployeeIdWatch = Form.useWatch("hasTcsEmployeeId", form);
  const personalEmailValue = Form.useWatch("personalEmailId", form);
  const microsoftAliasValue = Form.useWatch("microsoftAlias", form) as string | undefined;
  const countries: ICountry[] = countryProps?.queryResult?.data?.data ?? [];
  const countryConfig = selectedCountryId
    ? countries.find((country) => country?.id === selectedCountryId)
    : undefined;
  const countryRequiresTcs = countryConfig?.backgroundScreening?.requireTcsEmployeeId;
  const isTcsEmployeeIdMandatory = !selectedCountryId
    || countryRequiresTcs !== false;
  const showTcsIdToggle = !isTcsEmployeeIdMandatory;
  const hasTcsEmployeeId = isTcsEmployeeIdMandatory ? true : hasTcsEmployeeIdWatch !== false;
  const tcsEmployeeIdInputDisabled = showTcsIdToggle ? !hasTcsEmployeeId : false;

  useEffect(() => {
    if (!form) {
      return;
    }

    const trimmedFirst = (firstName ?? "").trim();
    const trimmedLast = (lastName ?? "").trim();
    const fullName = [trimmedFirst, trimmedLast].filter(Boolean).join(" ");
    const currentName = form.getFieldValue("name");

    if (currentName !== fullName) {
      form.setFieldsValue({ name: fullName });
    }
  }, [firstName, lastName, form]);

  useEffect(() => {
    if (!form) {
      return;
    }

    if (isTcsEmployeeIdMandatory && hasTcsEmployeeIdWatch !== true) {
      form.setFieldsValue({ hasTcsEmployeeId: true });
    }
  }, [form, isTcsEmployeeIdMandatory, hasTcsEmployeeIdWatch]);

  useEffect(() => {
    if (!form) {
      return;
    }

    if (hasTcsEmployeeId) {
      form.setFieldsValue({ personalEmailId: undefined });
      form.resetFields?.(["personalEmailId"]);
    } else {
      form.setFieldsValue({ tcsEmailId: undefined });
      form.resetFields?.(["tcsEmailId"]);
    }
  }, [hasTcsEmployeeId, form]);

  useEffect(() => {
    if (!form) {
      return;
    }

    const currentAvailability = form.getFieldValue("hasTcsEmployeeId");
    if (typeof currentAvailability === "boolean") {
      return;
    }

    if (personalEmailValue && !isTcsEmployeeIdMandatory) {
      form.setFieldsValue({ hasTcsEmployeeId: false });
    } else {
      form.setFieldsValue({ hasTcsEmployeeId: true });
    }
  }, [form, personalEmailValue, isTcsEmployeeIdMandatory]);

  return (
    <>
      <Form.Item
        name="name"
        hidden
        rules={validationRules.associateName}
      >
        <Input />
      </Form.Item>

      <Row gutter={rowGutter}>
        <Col className="gutter-row" {...twoColumnLayout}>
          <Form.Item
            label="Associate First Name"
            name="firstName"
            rules={validationRules.firstName}
          >
            <Input placeholder="Enter First Name" />
          </Form.Item>
        </Col>
        <Col className="gutter-row" {...twoColumnLayout}>
          <Form.Item
            label="Associate Last Name"
            name="lastName"
            rules={validationRules.lastName}
          >
            <Input placeholder="Enter Last Name" />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={rowGutter}>
        <Col className="gutter-row" {...twoColumnLayout}>
          <CountryField form={form} countryProps={countryProps} />
        </Col>
        <Col className="gutter-row" {...twoColumnLayout}>
          <LocationField form={form} locations={locations} />
        </Col>
      </Row>

      <Row gutter={rowGutter}>
        <Col className="gutter-row" {...twoColumnLayout}>
          <TcsEmployeeIdField
            showToggle={showTcsIdToggle}
            required={showTcsIdToggle ? hasTcsEmployeeId : true}
            disabled={tcsEmployeeIdInputDisabled}
            disableToggle={!showTcsIdToggle || isTcsEmployeeIdMandatory}
          />
        </Col>
        <Col className="gutter-row" {...twoColumnLayout}>
          {hasTcsEmployeeId ? <TcsEmailIdField /> : <PersonalEmailField />}
        </Col>
      </Row>

      <Row gutter={rowGutter}>
        <Col className="gutter-row" {...twoColumnLayout}>
          <Typography.Title level={5}>TCS Manager</Typography.Title>
          <Typography.Text>
            <ShowUserCard id={form?.getFieldValue("managerUserId") || userObj?.id} />
          </Typography.Text>
          <Form.Item hidden label="TCS Manager" name={["managerUserId"]}>
            <Input />
          </Form.Item>
        </Col>
        <Col className="gutter-row" {...twoColumnLayout}>
          <WonField />
        </Col>
      </Row>
      <Row gutter={rowGutter}>
        <Col className="gutter-row" {...twoColumnLayout}>
          <MicrosoftAliasField />
        </Col>
        <Col className="gutter-row" {...twoColumnLayout}>
          <MsPersonnelNumberField required={!!microsoftAliasValue} />
        </Col>
      </Row>
      <RemarksField />
    </>
  );
};

// Document upload sections
export const DocumentUploadSection: React.FC<{
  onUploadChange: any;
  id?: string;
  required?: boolean;
  trainingRequired?: boolean;
  consentLabel?: string;
  buttonText?: string;
  trainingLabel?: string;
  style?: React.CSSProperties;
}> = ({
  onUploadChange,
  id,
  required = true,
  trainingRequired,
  consentLabel,
  buttonText,
  trainingLabel,
  style
}) => {
    const { twoColumnLayout, rowGutter } = formLayouts;
    const formInstance = (Form as any).useFormInstance?.();
    const hasTcsEmployeeId = Form.useWatch?.("hasTcsEmployeeId", formInstance);
    const showCompletedTrainingUpload = hasTcsEmployeeId !== false;
    const isConsentRequired = required;
    const isTrainingRequired = typeof trainingRequired === "boolean" ? trainingRequired : showCompletedTrainingUpload;
    const consentFieldLabel = consentLabel ?? (isConsentRequired ? "Consent Document" : "Upload New Consent Document (Optional)");
    const trainingFieldLabel = trainingLabel ?? (isTrainingRequired ? "Completed Training Document" : "Completed Training Document (Optional)");
    const consentButtonText = buttonText ?? (isConsentRequired ? "Upload Document" : "Replace Document");
    const trainingButtonText = buttonText ?? "Upload Document";

    useEffect(() => {
      if (hasTcsEmployeeId === false) {
        formInstance?.setFieldsValue?.({ completedTrainingDocument: [] });
      }
    }, [hasTcsEmployeeId, formInstance]);

    return (
      <>
        <Row gutter={rowGutter} style={style}>
          <Col className="gutter-row" {...twoColumnLayout}>
            <DocumentUploadField
              name="consentDocument"
              label={consentFieldLabel}
              buttonText={consentButtonText}
              onUploadChange={onUploadChange}
              required={isConsentRequired}
              id={id}
            />
          </Col>
          {showCompletedTrainingUpload && (
            <Col className="gutter-row" {...twoColumnLayout}>
              <DocumentUploadField
                name="completedTrainingDocument"
                label={trainingFieldLabel}
                buttonText={trainingButtonText}
                onUploadChange={onUploadChange}
                required={isTrainingRequired}
                id={id}
              />
            </Col>
          )}
        </Row>

      </>
    );
  };
